import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiGetEmployeeAuditLog, type AuditLogEntry } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History, ArrowRight, Plus, Trash2, Pencil } from "lucide-react";
import type { EmployeeProfile } from "@/data/employees";

interface Props {
  employee: EmployeeProfile;
}

const ACTION_ICONS: Record<string, React.ElementType> = {
  update: Pencil,
  create: Plus,
  delete: Trash2,
};

const ACTION_COLORS: Record<string, string> = {
  update: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  create: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  delete: "bg-red-500/10 text-red-600 border-red-500/20",
};

function formatFieldName(field: string): string {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSectionName(section: string, t: (key: string) => string): string {
  const sectionMap: Record<string, string> = {
    employee_profile: t("audit.sections.employeeProfile"),
    bank_details: t("audit.sections.bankDetails"),
    salary: t("audit.sections.salary"),
    visa: t("audit.sections.visa"),
    emergency_contact: t("audit.sections.emergencyContact"),
    qualifications: t("audit.sections.qualifications"),
    equipment: t("audit.sections.equipment"),
  };
  return sectionMap[section] || section.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncateValue(val: string | null, maxLen = 60): string {
  if (!val) return "—";
  return val.length > maxLen ? val.slice(0, maxLen) + "…" : val;
}

interface GroupedEntry {
  changedAt: string;
  changedByName: string | null;
  changedByInitials: string | null;
  changedByAvatarColor: string | null;
  section: string;
  action: string;
  changes: { fieldName: string | null; oldValue: string | null; newValue: string | null }[];
}

function groupEntries(entries: AuditLogEntry[]): GroupedEntry[] {
  const groups: GroupedEntry[] = [];
  let current: GroupedEntry | null = null;

  for (const entry of entries) {
    const ts = new Date(entry.changedAt).getTime();
    if (
      current &&
      current.changedByName === entry.changedByName &&
      current.section === entry.section &&
      current.action === entry.action &&
      Math.abs(new Date(current.changedAt).getTime() - ts) < 2000
    ) {
      current.changes.push({
        fieldName: entry.fieldName,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
      });
    } else {
      current = {
        changedAt: entry.changedAt,
        changedByName: entry.changedByName,
        changedByInitials: entry.changedByInitials,
        changedByAvatarColor: entry.changedByAvatarColor,
        section: entry.section,
        action: entry.action,
        changes: [
          {
            fieldName: entry.fieldName,
            oldValue: entry.oldValue,
            newValue: entry.newValue,
          },
        ],
      };
      groups.push(current);
    }
  }

  return groups;
}

export function ProfileAuditTab({ employee }: Props) {
  const { t } = useTranslation();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["audit-log", employee.id],
    queryFn: () => apiGetEmployeeAuditLog(employee.id),
  });

  const grouped = groupEntries(entries);

  return (
    <div className="max-w-4xl">
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            {t("audit.title")}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {t("audit.description")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t("common.loading")}
            </p>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center italic">
              {t("audit.noChanges")}
            </p>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

              <div className="space-y-0">
                {grouped.map((group, idx) => {
                  const ActionIcon = ACTION_ICONS[group.action] || Pencil;
                  const realChanges = group.changes.filter(
                    (c) => c.oldValue !== c.newValue
                  );
                  if (realChanges.length === 0) return null;

                  return (
                    <div key={idx} className="relative pl-10 pb-6">
                      {/* Timeline dot */}
                      <div className="absolute left-2.5 top-1 h-3 w-3 rounded-full border-2 border-background bg-primary" />

                      {/* Header */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {group.changedByAvatarColor ? (
                          <div
                            className={`h-6 w-6 rounded-full bg-gradient-to-br ${group.changedByAvatarColor} flex items-center justify-center text-white text-[10px] font-semibold shrink-0`}
                          >
                            {group.changedByInitials || "?"}
                          </div>
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-[10px] font-semibold shrink-0">
                            ?
                          </div>
                        )}
                        <span className="text-sm font-medium">
                          {group.changedByName || t("audit.unknownUser")}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 gap-0.5 ${ACTION_COLORS[group.action] || ""}`}
                        >
                          <ActionIcon className="h-2.5 w-2.5" />
                          {t(`audit.action.${group.action}`)}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {formatSectionName(group.section, t)}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground ml-auto">
                          {new Date(group.changedAt).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>

                      {/* Changes table */}
                      <div className="rounded-md border bg-muted/30 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left font-medium px-3 py-1.5 text-muted-foreground w-1/4">
                                {t("audit.field")}
                              </th>
                              <th className="text-left font-medium px-3 py-1.5 text-muted-foreground w-[37.5%]">
                                {t("audit.oldValue")}
                              </th>
                              <th className="text-left font-medium px-3 py-1.5 text-muted-foreground w-0" />
                              <th className="text-left font-medium px-3 py-1.5 text-muted-foreground w-[37.5%]">
                                {t("audit.newValue")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {realChanges.map((change, cIdx) => (
                              <tr
                                key={cIdx}
                                className="border-b last:border-b-0"
                              >
                                <td className="px-3 py-1.5 font-medium text-foreground">
                                  {change.fieldName
                                    ? formatFieldName(change.fieldName)
                                    : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-muted-foreground">
                                  {group.action === "create" ? (
                                    <span className="italic">—</span>
                                  ) : (
                                    <span className="text-red-600/80 line-through">
                                      {truncateValue(change.oldValue)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-1 py-1.5">
                                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                </td>
                                <td className="px-3 py-1.5">
                                  {group.action === "delete" ? (
                                    <span className="italic text-muted-foreground">
                                      {t("audit.deleted")}
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 font-medium">
                                      {truncateValue(change.newValue)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
